import React, { useCallback, useEffect } from 'react'
import { Workbook } from 'exceljs'
import saveAs from 'file-saver'
import TanStackDatagrid, {
  TanStackDatagridColumn,
  TanStackDatagridProps
} from './TanStackDatagrid'
import './TanStack.css'

export type TanStackDatagridExportValue = React.ReactNode | Date
export type TanStackDatagridExportFileType = 'csv' | 'xlsx'

export interface TanStackDatagridExportColumn<TData>
  extends TanStackDatagridColumn<TData> {
  exportable?: boolean
  exportAccessor?: (row: TData) => TanStackDatagridExportValue
}

export interface TanStackDatagridWithExportProps<TData>
  extends Omit<TanStackDatagridProps<TData>, 'columns'> {
  columns: TanStackDatagridExportColumn<TData>[]
  exportButtonLabel?: string
  exportDisabled?: boolean
  exportFileName?: string
  exportFileType?: TanStackDatagridExportFileType
  exportRows?: TData[]
}

const availableFileTypes: TanStackDatagridExportFileType[] = ['xlsx', 'csv']

const escapeCsvValue = (value: string): string => {
  const escapedValue = value.replace(/"/g, '""')
  return /[",\n\r]/.test(escapedValue) ? `"${escapedValue}"` : escapedValue
}

const stringifyExportValue = (value: TanStackDatagridExportValue): string => {
  if (value === null || value === undefined || typeof value === 'boolean') {
    return ''
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  if (Array.isArray(value)) {
    return value.map(stringifyExportValue).filter(Boolean).join(' ')
  }

  if (React.isValidElement(value)) {
    const props = value.props as { children?: React.ReactNode }
    return stringifyExportValue(props.children)
  }

  return ''
}

const getColumnExportLabel = <TData,>(
  column: TanStackDatagridExportColumn<TData>
): string => {
  return stringifyExportValue(column.header) || column.id
}

const createCsvContent = <TData,>(
  rows: TData[],
  columns: TanStackDatagridExportColumn<TData>[]
): string => {
  const visibleColumns = columns.filter((column) => column.exportable !== false)
  const headerRow = visibleColumns.map((column) =>
    escapeCsvValue(getColumnExportLabel(column))
  )
  const dataRows = rows.map((row) =>
    visibleColumns.map((column) => {
      const value = column.exportAccessor
        ? column.exportAccessor(row)
        : column.accessor(row)
      return escapeCsvValue(stringifyExportValue(value))
    })
  )

  return [headerRow, ...dataRows].map((row) => row.join(',')).join('\n')
}

const getExportRows = <TData,>(
  rows: TData[],
  columns: TanStackDatagridExportColumn<TData>[]
): string[][] => {
  const visibleColumns = columns.filter((column) => column.exportable !== false)
  const headerRow = visibleColumns.map(getColumnExportLabel)
  const dataRows = rows.map((row) =>
    visibleColumns.map((column) => {
      const value = column.exportAccessor
        ? column.exportAccessor(row)
        : column.accessor(row)
      return stringifyExportValue(value)
    })
  )

  return [headerRow, ...dataRows]
}

const getExportFileName = (
  fileName: string,
  fileType: TanStackDatagridExportFileType
): string => {
  const baseFileName = fileName.replace(/\.(csv|xlsx)$/i, '')
  return `${baseFileName}.${fileType}`
}

const downloadCsv = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  link.click()

  URL.revokeObjectURL(url)
}

const downloadXlsx = async <TData,>(
  rows: TData[],
  columns: TanStackDatagridExportColumn<TData>[],
  fileName: string
) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet('Main sheet')

  getExportRows(rows, columns).forEach((row) => worksheet.addRow(row))

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(new Blob([buffer as BlobPart], { type: 'application/octet-stream' }), fileName)
}

const TanStackDatagridWithExport = <TData,>({
  columns,
  data,
  exportButtonLabel = 'Export ',
  exportDisabled = false,
  exportFileName = 'export.csv',
  exportFileType = 'csv',
  exportRows,
  toolbarContent,
  ...gridProps
}: TanStackDatagridWithExportProps<TData>): React.ReactElement => {
  const rows = exportRows || data
  const exportDisabledState = exportDisabled || rows.length === 0
  const dropdownRef = React.useRef<HTMLDetailsElement | null>(null)
  const sortedFileTypes = [
    exportFileType,
    ...availableFileTypes.filter((fileType) => fileType !== exportFileType)
  ]

  const handleExport = useCallback(async (fileType: TanStackDatagridExportFileType) => {
    const fileName = getExportFileName(exportFileName, fileType)

    try {
      if (fileType === 'xlsx') {
        await downloadXlsx(rows, columns, fileName)
        return
      }

      const csvContent = createCsvContent(rows, columns)
      downloadCsv(csvContent, fileName)
    } finally {
      if (dropdownRef.current) dropdownRef.current.open = false
    }
  }, [columns, exportFileName, rows])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dropdownRef.current?.open) {
        dropdownRef.current.open = false
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const targetNode = event.target as Node | null

      if (!targetNode || !dropdownRef.current?.open) return
      if (dropdownRef.current.contains(targetNode)) return

      dropdownRef.current.open = false
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  const exportDropdown = (
    <details className="tsdg-export-dropdown" ref={dropdownRef}>
      <summary
        aria-label={exportButtonLabel}
        aria-disabled={exportDisabledState}
        className="btn btn-sm btn-secondary tsdg-export-button"
        onClick={(event) => {
          if (exportDisabledState) event.preventDefault()
        }}
        role="button"
      >
        <span>{exportButtonLabel}</span>
        <span className="tsdg-export-caret" aria-hidden="true" />
      </summary>
      <div className="tsdg-export-menu">
        {sortedFileTypes.map((fileType) => (
          <button
            key={fileType}
            type="button"
            className="tsdg-export-menu-item"
            disabled={exportDisabledState}
            onClick={() => handleExport(fileType)}
          >
            .{fileType}
          </button>
        ))}
      </div>
    </details>
  )

  return (
    <TanStackDatagrid
      data={data}
      columns={columns}
      toolbarContent={toolbarContent}
      toolbarActions={exportDropdown}
      {...gridProps}
    />
  )
}

export default TanStackDatagridWithExport
